import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
// eslint-disable-next-line import/no-named-as-default
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodes,
  useReactFlow,
} from "reactflow";

import "reactflow/dist/style.css";
import { buildTreeFromTrace } from "./buildTree";
import { FeatureDocsContext, TaggedText, nodeTypes } from "./nodes";
import "./IrisDecisionTreeView.css";

// Normalizes trace data from the API response (handles double-nested structure)
function normalizeTrace(data) {
  if (!data) return null;
  const trace =
    data.decision_trace?.decision_trace ?? data.decision_trace ?? data;
  if (!trace) return null;
  // Normalize keys (strip whitespace)
  return JSON.parse(JSON.stringify(trace, (k, v) => v));
}

export default function IrisDecisionTreeView({
  traceApiData,
  featureDocs = {},
  asin,
  store,
}) {
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [tooltipPos, setTooltipPos] = useState(null);
  const [tooltipFeature, setTooltipFeature] = useState(null);
  const [allCollapsed, setAllCollapsed] = useState(false);
  const [showCatalog, setShowCatalog] = useState(false);

  const trace = normalizeTrace(traceApiData);
  const features = traceApiData?.features ?? {};
  const decision = traceApiData?.decision ?? {};

  useEffect(() => {
    const handler = (e) => setSelectedFeature(e.detail.name);
    window.addEventListener("iris:feature-click", handler);
    return () => window.removeEventListener("iris:feature-click", handler);
  }, []);

  const handleToggleAll = () => {
    const next = !allCollapsed;
    setAllCollapsed(next);
    window.dispatchEvent(
      new CustomEvent("iris:toggle-all", { detail: { collapsed: next } })
    );
  };

  const stableNodeTypes = useMemo(() => nodeTypes, []);
  const { nodes, edges } = buildTreeFromTrace({
    trace,
    asin,
    store,
    features,
    decision,
  });

  return (
    <FeatureDocsContext.Provider value={featureDocs}>
      <div className="iris-tree-root">
        <div className="iris-toolbar">
          <button
            className={`iris-toggle-all-btn${allCollapsed ? "" : ""}`}
            onClick={handleToggleAll}
          >
            {allCollapsed ? "▸ Expand all" : "▾ Collapse all"}
          </button>

          <button
            className={`iris-toggle-all-btn${
              showCatalog ? " iris-toggle-all-btn--active" : ""
            }`}
            onClick={() => setShowCatalog((s) => !s)}
          >
            {showCatalog ? "✕ Catalog" : "⊞ Catalog"}
          </button>

          <div className="iris-nav-legend">
            <NavHint label="Pan">
              <span className="iris-kbd">Space</span>
              <span className="iris-nav-plus">+</span>
              <span className="iris-kbd">Drag</span>
            </NavHint>
            <NavHint label="Pan">
              <span className="iris-kbd">Right-click</span>
              <span className="iris-nav-plus">+</span>
              <span className="iris-kbd">Drag</span>
            </NavHint>
            <NavHint label="Zoom">
              <span className="iris-kbd">Scroll</span>
            </NavHint>
          </div>
        </div>

        <div className="iris-main-content">
          <aside className="iris-features-sidebar">
            <div className="iris-sidebar-title">Features</div>
            {Object.entries(features).map(([key, val]) => {
              const doc = featureDocs.features?.[key];
              return (
                <div
                  key={key}
                  className={`iris-feature-row${
                    doc ? " iris-feature-row--clickable" : ""
                  }${
                    selectedFeature === key ? " iris-feature-row--active" : ""
                  }`}
                  onClick={() =>
                    doc &&
                    setSelectedFeature(selectedFeature === key ? null : key)
                  }
                  onMouseMove={
                    doc
                      ? (e) => {
                          setTooltipFeature(key);
                          setTooltipPos({ x: e.clientX, y: e.clientY });
                        }
                      : undefined
                  }
                  onMouseLeave={doc ? () => setTooltipPos(null) : undefined}
                >
                  <span className="iris-feature-key">{key}</span>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 4 }}
                  >
                    <span className="iris-feature-val">
                      {formatVal(key, val)}
                    </span>
                    {!doc && (
                      <span
                        title="Not documented in feature_docs.json"
                        style={{
                          color: "#ef4444",
                          fontSize: 12,
                          lineHeight: 1,
                          flexShrink: 0,
                        }}
                      >
                        ⚠
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
            {tooltipPos &&
              tooltipFeature &&
              featureDocs.features?.[tooltipFeature] &&
              createPortal(
                <div
                  style={{
                    position: "fixed",
                    top: tooltipPos.y - 12,
                    left: tooltipPos.x + 16,
                    background: "#1e3a5f",
                    color: "#fff",
                    padding: "7px 11px",
                    borderRadius: 7,
                    fontSize: 11,
                    zIndex: 9999,
                    pointerEvents: "none",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
                    maxWidth: 260,
                    lineHeight: 1.45,
                    fontFamily:
                      '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                  }}
                >
                  <TaggedText
                    text={
                      featureDocs.features[tooltipFeature].short_description
                    }
                    features={features}
                    inFlow={false}
                  />
                </div>,
                document.body
              )}
          </aside>

          <div className="iris-flow-area">
            <ReactFlow
              key={asin}
              defaultNodes={nodes}
              defaultEdges={edges}
              nodeTypes={stableNodeTypes}
              fitView
              fitViewOptions={{ padding: 0.15 }}
              nodesDraggable={false}
              nodesConnectable={false}
              elementsSelectable={false}
              panOnDrag={[1, 2]}
              panActivationKeyCode="Space"
              proOptions={{ hideAttribution: true }}
            >
              <LayoutUpdater />
              <Background color="#e2e8f0" gap={24} size={1} />
              <Controls showInteractive={false} />
              <MiniMap
                nodeColor={(n) => n.data?.color ?? "#e2e8f0"}
                maskColor="rgba(241,245,249,0.7)"
                style={{
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: 8,
                }}
              />
            </ReactFlow>
          </div>
        </div>

        {selectedFeature && featureDocs.features?.[selectedFeature] && (
          <FeatureDetailPanel
            name={selectedFeature}
            doc={featureDocs.features?.[selectedFeature]}
            onClose={() => setSelectedFeature(null)}
            features={features}
            rightOffset={showCatalog ? 380 : 16}
          />
        )}

        {showCatalog && (
          <FeatureCatalogPanel
            traceFeatures={features}
            onClose={() => setShowCatalog(false)}
          />
        )}

        <div className="iris-legend">
          <LegendItem color="#16a34a" bg="#f0fdf4" label="Active rule (HIT)" />
          <LegendItem
            color="#94a3b8"
            bg="#f8fafc"
            label="Inactive rule (NO HIT)"
          />
          <LegendItem color="#15803d" bg="#dcfce7" label="Winning rule" />
          <LegendItem
            color="#3b82f6"
            bg="#eff6ff"
            label="Selected path"
            dashed
          />
          <LegendItem color="#dc2626" bg="#fef2f2" label="Very low inventory" />
          <div className="iris-legend-item">
            <span
              style={{
                color: "#ef4444",
                fontSize: 14,
                lineHeight: 1,
                width: 20,
                textAlign: "center",
                flexShrink: 0,
              }}
            >
              ⚠
            </span>
            <span className="iris-legend-label">Undocumented feature</span>
          </div>
        </div>
      </div>
    </FeatureDocsContext.Provider>
  );
}

// Watches node.height (reactflow v11 equivalent of node.measured.height in v12)
// and recomputes Y positions dynamically after first render.
function LayoutUpdater() {
  const nodes = useNodes();
  const { setNodes } = useReactFlow();
  const prevHeights = useRef({});

  useEffect(() => {
    const heights = {};
    let changed = false;
    nodes.forEach((n) => {
      const h = n.height;
      if (h != null) {
        heights[n.id] = h;
        if (prevHeights.current[n.id] !== h) changed = true;
      }
    });
    if (!changed) return;
    prevHeights.current = heights;

    const GAP = 40;
    const RULE_ROW_Y = 260;

    const ruleMaxH = Math.max(
      ...nodes
        .filter((n) => n.id.startsWith("rule-"))
        .map((n) => heights[n.id] ?? 100),
      100
    );

    const prioritizeY = RULE_ROW_Y + ruleMaxH + GAP;
    const actionY = prioritizeY + (heights["prioritize"] ?? 0) + GAP;
    const resultY = actionY + (heights["action"] ?? 0) + GAP;

    const posMap = {
      prioritize: prioritizeY,
      action: actionY,
      result: resultY,
    };

    setNodes((nds) =>
      nds.map((n) =>
        posMap[n.id] !== undefined
          ? { ...n, position: { x: n.position.x, y: posMap[n.id] } }
          : n
      )
    );
  }, [nodes, setNodes]);

  return null;
}

function FeatureDetailPanel({
  name,
  doc,
  onClose,
  features,
  rightOffset = 16,
}) {
  return (
    <div
      style={{
        position: "absolute",
        top: 60,
        right: rightOffset,
        transition: "right 0.2s ease",
        width: 340,
        maxHeight: "calc(100% - 100px)",
        overflowY: "auto",
        background: "#fff",
        border: "1.5px solid #e2e8f0",
        borderRadius: 12,
        boxShadow: "0 8px 32px rgba(0,0,0,0.13)",
        zIndex: 1000,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          padding: "12px 14px 10px",
          borderBottom: "1px solid #e2e8f0",
          position: "sticky",
          top: 0,
          background: "#fff",
          borderRadius: "12px 12px 0 0",
        }}
      >
        <div>
          <div
            style={{
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "#94a3b8",
              marginBottom: 2,
            }}
          >
            Feature
          </div>
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: "#1e3a5f",
              lineHeight: 1.3,
            }}
          >
            {name}
          </div>
          {doc.short_description && (
            <div
              style={{
                fontSize: 11,
                color: "#64748b",
                marginTop: 3,
                lineHeight: 1.4,
              }}
            >
              <TaggedText
                text={doc.short_description}
                features={features}
                inFlow={false}
              />
            </div>
          )}
        </div>
        <button
          onClick={onClose}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "#94a3b8",
            fontSize: 18,
            lineHeight: 1,
            padding: "0 0 0 8px",
            flexShrink: 0,
          }}
        >
          ×
        </button>
      </div>

      <div
        style={{
          padding: "12px 14px",
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        <DocSection label="Description">
          <p
            style={{
              margin: 0,
              fontSize: 11,
              color: "#374151",
              lineHeight: 1.55,
            }}
          >
            <TaggedText
              text={doc.long_description}
              features={features}
              inFlow={false}
            />
          </p>
        </DocSection>

        {doc.formula && (
          <DocSection label="Formula">
            <code
              style={{
                display: "block",
                fontSize: 10,
                fontFamily: "SFMono-Regular, Consolas, monospace",
                background: "#f1f5f9",
                border: "1px solid #e2e8f0",
                borderRadius: 6,
                padding: "6px 8px",
                color: "#1e3a5f",
                lineHeight: 1.5,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              <TaggedText
                text={doc.formula}
                features={features}
                inFlow={false}
              />
            </code>
          </DocSection>
        )}

        {doc.data_sources?.length > 0 && (
          <DocSection label="Data Sources">
            <ul
              style={{
                margin: 0,
                paddingLeft: 16,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              {doc.data_sources.map((s, i) => (
                <li
                  key={i}
                  style={{
                    fontSize: 10,
                    color: "#374151",
                    fontFamily: "SFMono-Regular, Consolas, monospace",
                    lineHeight: 1.5,
                    textTransform: "none",
                  }}
                >
                  <span style={{ textTransform: "none" }}>
                    <TaggedText
                      text={s.source_name}
                      features={features}
                      inFlow={false}
                    />
                  </span>{" "}
                  <span
                    style={{
                      color: "#94a3b8",
                      fontFamily:
                        '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                      textTransform: "none",
                    }}
                  >
                    ({s.source_type})
                  </span>
                </li>
              ))}
            </ul>
          </DocSection>
        )}

        {doc.depends_on?.length > 0 && (
          <DocSection label="Depends On">
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {doc.depends_on.map((d, i) => (
                <span
                  key={i}
                  onClick={() =>
                    window.dispatchEvent(
                      new CustomEvent("iris:feature-click", {
                        detail: { name: d },
                      })
                    )
                  }
                  style={{
                    fontSize: 10,
                    background: "#eff6ff",
                    color: "#1d4ed8",
                    border: "1px solid #bfdbfe",
                    borderRadius: 4,
                    padding: "1px 6px",
                    fontFamily: "SFMono-Regular, Consolas, monospace",
                    cursor: "pointer",
                  }}
                >
                  {d}
                </span>
              ))}
            </div>
          </DocSection>
        )}
      </div>
    </div>
  );
}

function DocSection({ label, children }) {
  return (
    <div>
      <div
        style={{
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: "0.07em",
          textTransform: "uppercase",
          color: "#94a3b8",
          marginBottom: 5,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

function FeatureCatalogPanel({ traceFeatures, onClose }) {
  const featureDocs = useContext(FeatureDocsContext);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);

  const all = Object.entries(featureDocs.features || {}).sort(([a], [b]) =>
    a.localeCompare(b)
  );
  const filtered = search.trim()
    ? all.filter(
        ([k, doc]) =>
          k.toLowerCase().includes(search.toLowerCase()) ||
          doc.short_description?.toLowerCase().includes(search.toLowerCase())
      )
    : all;

  return (
    <div
      style={{
        position: "absolute",
        top: 48,
        right: 0,
        bottom: 0,
        width: 360,
        background: "#fff",
        borderLeft: "1.5px solid #e2e8f0",
        display: "flex",
        flexDirection: "column",
        zIndex: 900,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        boxShadow: "-4px 0 20px rgba(0,0,0,0.08)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 16px",
          borderBottom: "1px solid #e2e8f0",
          flexShrink: 0,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "#94a3b8",
              marginBottom: 2,
            }}
          >
            Feature Catalog
          </div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#1e3a5f" }}>
            {all.length} features documented
          </div>
        </div>
        <button
          onClick={onClose}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "#94a3b8",
            fontSize: 20,
            lineHeight: 1,
            padding: 0,
          }}
        >
          ×
        </button>
      </div>

      <div
        style={{
          padding: "10px 14px",
          borderBottom: "1px solid #f1f5f9",
          flexShrink: 0,
        }}
      >
        <input
          type="text"
          placeholder="Search by name or description…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            width: "100%",
            padding: "6px 10px",
            border: "1.5px solid #e2e8f0",
            borderRadius: 7,
            fontSize: 12,
            fontFamily: "inherit",
            color: "#1e3a5f",
            outline: "none",
            boxSizing: "border-box",
          }}
        />
        {search && (
          <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 5 }}>
            {filtered.length} of {all.length} features
          </div>
        )}
      </div>

      <div style={{ flex: 1, overflowY: "auto" }}>
        {filtered.length === 0 ? (
          <div
            style={{
              padding: "24px 16px",
              fontSize: 12,
              color: "#94a3b8",
              textAlign: "center",
            }}
          >
            No features match
          </div>
        ) : (
          filtered.map(([key, doc]) => {
            const inTrace = traceFeatures && key in traceFeatures;
            const traceValue = inTrace ? traceFeatures[key] : undefined;
            const isExpanded = expanded === key;
            const plainShort = (doc.short_description || "").replace(
              /<[^>]+>/g,
              ""
            );

            return (
              <div key={key} style={{ borderBottom: "1px solid #f1f5f9" }}>
                <button
                  onClick={() => setExpanded(isExpanded ? null : key)}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 8,
                    width: "100%",
                    background: isExpanded ? "#f8faff" : "none",
                    border: "none",
                    padding: "9px 14px",
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  <span
                    style={{
                      fontSize: 9,
                      color: "#94a3b8",
                      marginTop: 3,
                      flexShrink: 0,
                      width: 8,
                    }}
                  >
                    {isExpanded ? "▾" : "▸"}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        flexWrap: "wrap",
                      }}
                    >
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: "#1e3a5f",
                          fontFamily: "SFMono-Regular, Consolas, monospace",
                        }}
                      >
                        {key}
                      </span>
                      {inTrace ? (
                        <span
                          style={{
                            fontSize: 10,
                            background: "#dcfce7",
                            color: "#15803d",
                            border: "1px solid #86efac",
                            borderRadius: 4,
                            padding: "0 5px",
                            fontWeight: 600,
                            flexShrink: 0,
                          }}
                        >
                          {formatVal(key, traceValue)}
                        </span>
                      ) : (
                        <span
                          style={{
                            fontSize: 10,
                            color: "#cbd5e1",
                            fontStyle: "italic",
                            flexShrink: 0,
                          }}
                        >
                          not in trace
                        </span>
                      )}
                    </div>
                    {!isExpanded && plainShort && (
                      <div
                        style={{
                          fontSize: 10,
                          color: "#64748b",
                          marginTop: 2,
                          lineHeight: 1.4,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {plainShort.length > 72
                          ? plainShort.slice(0, 72) + "…"
                          : plainShort}
                      </div>
                    )}
                  </div>
                </button>

                {isExpanded && (
                  <div
                    style={{
                      padding: "2px 14px 14px 30px",
                      display: "flex",
                      flexDirection: "column",
                      gap: 10,
                    }}
                  >
                    {doc.short_description && (
                      <div
                        style={{
                          fontSize: 11,
                          color: "#64748b",
                          lineHeight: 1.5,
                        }}
                      >
                        <TaggedText
                          text={doc.short_description}
                          features={traceFeatures}
                          inFlow={false}
                        />
                      </div>
                    )}

                    {doc.long_description && (
                      <CatalogSection label="Description">
                        <p
                          style={{
                            margin: 0,
                            fontSize: 11,
                            color: "#374151",
                            lineHeight: 1.55,
                          }}
                        >
                          <TaggedText
                            text={doc.long_description}
                            features={traceFeatures}
                            inFlow={false}
                          />
                        </p>
                      </CatalogSection>
                    )}

                    {doc.formula && (
                      <CatalogSection label="Formula">
                        <code
                          style={{
                            display: "block",
                            fontSize: 10,
                            fontFamily: "SFMono-Regular, Consolas, monospace",
                            background: "#f1f5f9",
                            border: "1px solid #e2e8f0",
                            borderRadius: 6,
                            padding: "5px 8px",
                            color: "#1e3a5f",
                            lineHeight: 1.5,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                          }}
                        >
                          <TaggedText
                            text={doc.formula}
                            features={traceFeatures}
                            inFlow={false}
                          />
                        </code>
                      </CatalogSection>
                    )}

                    {doc.data_sources?.length > 0 && (
                      <CatalogSection label="Data Sources">
                        <ul
                          style={{
                            margin: 0,
                            paddingLeft: 14,
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                          }}
                        >
                          {doc.data_sources.map((s, i) => (
                            <li
                              key={i}
                              style={{
                                fontSize: 10,
                                color: "#374151",
                                fontFamily:
                                  "SFMono-Regular, Consolas, monospace",
                                lineHeight: 1.5,
                              }}
                            >
                              <span style={{ textTransform: "none" }}>
                                <TaggedText
                                  text={s.source_name}
                                  features={traceFeatures}
                                  inFlow={false}
                                />
                              </span>{" "}
                              <span
                                style={{
                                  color: "#94a3b8",
                                  fontFamily:
                                    '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                                }}
                              >
                                ({s.source_type})
                              </span>
                            </li>
                          ))}
                        </ul>
                      </CatalogSection>
                    )}

                    {doc.depends_on?.length > 0 && (
                      <CatalogSection label="Depends On">
                        <div
                          style={{ display: "flex", flexWrap: "wrap", gap: 4 }}
                        >
                          {doc.depends_on.map((d, i) => (
                            <span
                              key={i}
                              onClick={() =>
                                window.dispatchEvent(
                                  new CustomEvent("iris:feature-click", {
                                    detail: { name: d },
                                  })
                                )
                              }
                              style={{
                                fontSize: 10,
                                background: "#eff6ff",
                                color: "#1d4ed8",
                                border: "1px solid #bfdbfe",
                                borderRadius: 4,
                                padding: "1px 6px",
                                fontFamily:
                                  "SFMono-Regular, Consolas, monospace",
                                cursor: "pointer",
                              }}
                            >
                              {d}
                            </span>
                          ))}
                        </div>
                      </CatalogSection>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function CatalogSection({ label, children }) {
  return (
    <div>
      <div
        style={{
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: "0.07em",
          textTransform: "uppercase",
          color: "#94a3b8",
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

function formatVal(key, val) {
  if (val == null) return "—";
  if (typeof val === "boolean") return val ? "Yes" : "No";
  const k = key.toLowerCase();
  if (k.includes("price") || k.includes("cost") || k.includes("budget"))
    return `$${Number(val).toFixed(2)}`;
  if (k.includes("acos")) return `${(val * 100).toFixed(1)}%`;
  if (typeof val === "number")
    return Number.isInteger(val) ? String(val) : val.toFixed(2);
  return String(val);
}

function NavHint({ label, children }) {
  return (
    <div className="iris-nav-item">
      <span className="iris-nav-label">{label}</span>
      <span className="iris-nav-keys">{children}</span>
    </div>
  );
}

function LegendItem({ color, bg, label, dashed }) {
  return (
    <div className="iris-legend-item">
      {dashed ? (
        <svg width="28" height="12" style={{ flexShrink: 0 }}>
          <line
            x1="2"
            y1="6"
            x2="26"
            y2="6"
            stroke={color}
            strokeWidth="2.5"
            strokeDasharray="4 3"
          />
        </svg>
      ) : (
        <div
          className="iris-legend-swatch"
          style={{ background: bg, border: `2px solid ${color}` }}
        />
      )}
      <span className="iris-legend-label">{label}</span>
    </div>
  );
}
